import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { MultiSelect } from '@/components/ui/multiselect';
import { Separator } from '@/components/ui/separator';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowUpDown, Filter, X, Check } from 'lucide-react';

// ─── Filter types ─────────────────────────────────────────────────────

export interface TeamMembersFilters {
  departments: string[];
  roles: string[];
  statuses: string[];
}

export const EMPTY_TEAM_FILTERS: TeamMembersFilters = {
  departments: [],
  roles: [],
  statuses: [],
};

/** Count the number of active (non-empty) filter categories */
export function countActiveTeamFilters(filters: TeamMembersFilters): number {
  let count = 0;
  if (filters.departments.length > 0) count++;
  if (filters.roles.length > 0) count++;
  if (filters.statuses.length > 0) count++;
  return count;
}

/** Check if any filter is active */
export function hasActiveTeamFilters(filters: TeamMembersFilters): boolean {
  return countActiveTeamFilters(filters) > 0;
}

// ─── Component ────────────────────────────────────────────────────────

interface TeamMembersFilterSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appliedFilters: TeamMembersFilters;
  onApply: (filters: TeamMembersFilters) => void;
  onClear: () => void;
  availableOptions: {
    departments: { value: string; label: string }[];
    roles: { value: string; label: string }[];
    statuses: { value: string; label: string }[];
  };
  sortBy: string;
  onSortChange: (value: string) => void;
}

export function TeamMembersFilterSheet({
  open,
  onOpenChange,
  appliedFilters,
  onApply,
  onClear,
  availableOptions,
  sortBy,
  onSortChange,
}: TeamMembersFilterSheetProps) {
  const [draft, setDraft] = useState<TeamMembersFilters>(EMPTY_TEAM_FILTERS);

  // Sync draft from applied filters when sheet opens
  useEffect(() => {
    if (open) {
      setDraft({ ...appliedFilters });
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Draft helpers ──

  const updateDraftField = useCallback(
    <K extends keyof TeamMembersFilters>(key: K, value: TeamMembersFilters[K]) => {
      setDraft((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const clearDraft = useCallback(() => {
    setDraft(EMPTY_TEAM_FILTERS);
  }, []);

  const draftActiveCount = useMemo(() => countActiveTeamFilters(draft), [draft]);
  const appliedActiveCount = useMemo(
    () => countActiveTeamFilters(appliedFilters),
    [appliedFilters]
  );

  // ── Handlers ──

  const handleApply = () => {
    onApply(draft);
    onOpenChange(false);
  };

  const handleClearAll = () => {
    clearDraft();
    onClear();
  };

  // ── Filter sections config ──

  const sections: {
    key: keyof TeamMembersFilters;
    label: string;
    options: { value: string; label: string }[];
    placeholder: string;
  }[] = [
    {
      key: 'departments',
      label: 'Department',
      options: availableOptions.departments,
      placeholder: 'Select departments...',
    },
    {
      key: 'roles',
      label: 'Role',
      options: availableOptions.roles,
      placeholder: 'Select roles...',
    },
    {
      key: 'statuses',
      label: 'Status',
      options: availableOptions.statuses,
      placeholder: 'Select statuses...',
    },
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[450px] sm:w-[450px] sm:max-w-none p-0 flex flex-col gap-0 overflow-hidden"
      >
        {/* ── Fixed Header ── */}
        <SheetHeader className="px-6 pt-4 pb-3 flex-shrink-0 border-b">
          <SheetTitle className="text-base flex items-center gap-2">
            Filters
            <Badge variant="secondary" className="ml-2 flex items-center gap-1.5 px-2.5 py-0.5">
              <Filter className="h-3 w-3" />
              {appliedActiveCount} {appliedActiveCount === 1 ? 'filter' : 'filters'} active
            </Badge>
          </SheetTitle>
        </SheetHeader>

        {/* ── Scrollable Content ── */}
        <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4 space-y-3">
          {/* Sort section */}
          <div className="space-y-3">
            <Label className="text-xs font-semibold flex items-center gap-1.5">
              <ArrowUpDown className="h-3.5 w-3.5" />
              Sort By
            </Label>
            <Select value={sortBy} onValueChange={onSortChange}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="department">Department</SelectItem>
                <SelectItem value="role">Role</SelectItem>
                <SelectItem value="email">Email</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Separator className="mb-5" />

          {sections.map((section, idx) => {
            const isActive = draft[section.key].length > 0;
            return (
              <div key={section.key}>
                {idx > 0 && <Separator className="mb-5" />}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold">{section.label}</Label>
                    {isActive && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs text-muted-foreground"
                        onClick={() => updateDraftField(section.key, [])}
                      >
                        <X className="h-3 w-3 mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                  <MultiSelect
                    options={section.options}
                    placeholder={section.placeholder}
                    emptyMessage="No options found."
                    maxDisplay={2}
                    value={draft[section.key]}
                    onValueChange={(values) => updateDraftField(section.key, values)}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Fixed Footer ── */}
        <Separator className="flex-shrink-0" />
        <SheetFooter className="px-6 py-4 flex-shrink-0 flex-row items-center justify-between sm:justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearAll}
            className="gap-2"
            disabled={draftActiveCount === 0}
          >
            <X className="h-4 w-4" />
            Clear All
          </Button>
          <Button onClick={handleApply} className="gap-2">
            <Check className="h-4 w-4" />
            Apply Filters
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
